import { useState } from 'react';
import confetti from 'canvas-confetti';
import { Check, Hourglass, RotateCcw, Sparkles, type LucideIcon } from 'lucide-react';
import type { Rating, RatingPreview } from '../../../sdk/@types/Revisao';
import { confeteNaNota, movimentoDaNota, type MovimentoNota } from './movimentoDaNota';

/** Fields this bar reads from a queue preview. The due instant is intentionally
 *  absent: the API names it `dueDate` and RatingPreview names it `nextDueDate`.
 *  This control announces the interval, never the date, and never a schedule
 *  computed here. */
export type RatingIntervalSource = Pick<RatingPreview, 'intervalDays' | 'learnMinutes'>;

export interface RatingBarProps {
  onRate: (rating: Rating) => void;
  /** True while an answer is being recorded. All four ratings stay unavailable. */
  submitting?: boolean;
  className?: string;
  /** Intervals from the queue read. Absent, or a rating missing from the map,
   *  means that control announces no interval. */
  previews?: Partial<Record<Rating, RatingIntervalSource>>;
}

/**
 * Text for one queue preview, or null when there is nothing to announce.
 *
 * learnMinutes wins over intervalDays, including when intervalDays is 0: the
 * back uses a 0-day interval only beside a same-session return, so "0 dias"
 * would invent a schedule this file does not compute.
 */
export function formatRatingInterval(preview: RatingIntervalSource | undefined): string | null {
  if (preview == null) return null;

  const minutes = preview.learnMinutes;
  if (typeof minutes === 'number' && Number.isFinite(minutes) && minutes > 0) {
    if (minutes < 1) return '< 1 min';
    const n = Math.round(minutes);
    return n === 1 ? '1 min' : `${n} min`;
  }

  const days = preview.intervalDays;
  if (typeof days === 'number' && Number.isFinite(days) && days > 0) {
    return days === 1 ? '1 dia' : `${days} dias`;
  }

  return null;
}

const RATING_BASE: { rating: Rating; label: string; key: string; icon: LucideIcon; className: string }[] = [
  {
    rating: 'again',
    label: 'Errei',
    key: '1',
    icon: RotateCcw,
    className:
      'border-rate-again bg-rate-again/[0.12] text-rate-again hover:bg-rate-again/[0.22] disabled:hover:bg-rate-again/[0.12]',
  },
  {
    rating: 'hard',
    label: 'Difícil',
    key: '2',
    icon: Hourglass,
    className:
      'border-rate-hard bg-rate-hard/[0.12] text-rate-hard hover:bg-rate-hard/[0.22] disabled:hover:bg-rate-hard/[0.12]',
  },
  {
    rating: 'good',
    label: 'Bom',
    key: '3',
    icon: Check,
    className:
      'border-rate-good bg-rate-good/[0.12] text-rate-good hover:bg-rate-good/[0.22] disabled:hover:bg-rate-good/[0.12]',
  },
  {
    rating: 'easy',
    label: 'Fácil',
    key: '4',
    icon: Sparkles,
    // rate-easy on the light canvas is 1.2:1, so the label and the edge would
    // disappear (revisaoSurfaces.ts, ConclusaoSessao). Light uses on-lime for
    // those two (~18:1); the soft fill stays rate-easy in both themes. Dark
    // clears with rate-easy itself (~10:1 on the dark wash).
    className:
      'border-on-lime bg-rate-easy/[0.12] text-on-lime hover:bg-rate-easy/[0.22] disabled:hover:bg-rate-easy/[0.12] dark:border-rate-easy dark:text-rate-easy',
  },
];

/** `RATING_BASE` plus the one-shot CSS movement each button plays on click, read from
 *  `movimentoDaNota` — the table carries the movement, but never types its class name
 *  a second time here. `easy` resolves to `undefined` here on purpose: its movement is
 *  the full-screen confetti celebration below (`confeteNaNota`, `dispararConfete`), not
 *  a class on this button. */
const RATINGS: ({ rating: Rating; label: string; key: string; icon: LucideIcon; className: string } & {
  movimento: MovimentoNota | undefined;
})[] = RATING_BASE.map((r) => ({ ...r, movimento: movimentoDaNota(r.rating) }));

const BUTTON_CLASS =
  'flex min-h-11 min-w-0 flex-col items-center justify-center gap-1 border rounded-card px-3 py-2.5 text-center text-sm font-semibold leading-tight disabled:cursor-not-allowed disabled:opacity-50';

// Same chip language as ListaAtalhosSessao's <kbd> (SessaoControles.tsx), minus
// its neutral surface fill: this chip sits on each button's own tinted
// background, so it borrows the button's ink via `current` instead of a
// second, per-rating fill color. The `/40` modifier cannot be used on
// `currentColor`, because it does not generate and the border falls back to the default gray.
const KEY_CHIP_CLASS =
  'inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-chip border border-current px-1 font-mono text-[10px] font-normal leading-none';

// Same legacy palette as CarrosselForm.celebrar() (CarrosselForm.tsx:402-422), the
// product's other canvas-confetti call site. Copied, not imported: that function isn't
// exported, and this table's whole point (movimentoDaNota.ts, confeteNaNota) is that
// moving the celebration to a different rating never has to touch CarrosselForm.tsx.
const CONFETE_CORES = ['#2f6cd2', '#5b8def', '#2d9e6e', '#82ca9d', '#a3e635'];

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * The "Fácil" celebration — same call shape as `CarrosselForm.celebrar()`:
 * `disableForReducedMotion: true`, plus an explicit pre-check, so that under reduced
 * motion `canvas-confetti` is never called at all rather than called-but-silenced; and
 * `zIndex: 950`, the same figure `celebrar()` uses, already clear of every overlay this
 * screen renders (the batch bar, editor sheet and filter sheet all sit at 40–50).
 */
function dispararConfete() {
  if (prefersReducedMotion()) return;
  confetti({
    colors: CONFETE_CORES,
    disableForReducedMotion: true,
    zIndex: 950,
    particleCount: 80,
    spread: 70,
    startVelocity: 42,
    ticks: 150,
    origin: { y: 0.7 },
  });
}

/**
 * The question, the keys-and-gesture hint, and the four SM-2 rating buttons
 * (flashcard.pen, A1PII: "Como você se saiu?" / "teclas 1 – 4 · ou deslize" —
 * "deslize" names the existing swipe-to-skip gesture, never a swipe-to-rate
 * one the app does not have).
 *
 * `rc-rating` stays on the button group itself, not the outer wrapper: its
 * CSS (ReviewCard.css) staggers by `:nth-child` over exactly four direct
 * children, one per button. Moving the class up would collapse that
 * four-way stagger into two (the hint row, then the whole grid as one block).
 * Each control announces the interval the queue already computed; this file
 * does not schedule.
 */
export default function RatingBar({
  onRate,
  submitting = false,
  className = '',
  previews,
}: RatingBarProps) {
  // Which rating's movement is currently playing, if any — cleared by that same
  // button's own `animationend`, never a timer guessing the animation's length.
  const [disparando, setDisparando] = useState<Rating | null>(null);
  // This bar's buttons never remount between cards (RevisaoEstudar renders one
  // RatingBar instance per session, not per card), and `.rc-rating > *` keeps
  // claiming `animation: rc-rise` on every button forever. Clearing `disparando`
  // back to an *empty* class after the first click would hand the button back to
  // that rule — a genuinely different animation-name — and the browser would
  // replay the mount fade-in as a glitch after every single rating, for the rest
  // of the session. Once any movement has ever fired, idle buttons get an
  // explicit `rc-move-rest` (`animation: none`) instead of nothing, so the
  // fallback never lands back on `rc-rise`. Before the first click, this stays
  // false on purpose so the real mount reveal still plays once, untouched.
  const [movimentoJaDisparou, setMovimentoJaDisparou] = useState(false);

  return (
    <div className={className}>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 px-1">
        <span className="text-sm font-semibold text-ink">Como você se saiu?</span>
        <span className="text-xs text-ink-muted">teclas 1–4 · ou deslize</span>
      </div>
      <div
        role="group"
        aria-label="Avaliar cartão"
        aria-busy={submitting ? true : undefined}
        className="rc-rating grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3"
      >
        {RATINGS.map((r) => {
          const interval = formatRatingInterval(previews?.[r.rating]);
          const name = interval
            ? `${r.label}, tecla ${r.key}, ${interval}`
            : `${r.label}, tecla ${r.key}`;
          const Icon = r.icon;
          return (
            <button
              key={r.rating}
              type="button"
              data-rating={r.rating}
              aria-label={name}
              aria-keyshortcuts={r.key}
              disabled={submitting}
              onClick={(e) => {
                e.stopPropagation();
                if (submitting) return;
                if (r.movimento) {
                  setDisparando(r.rating);
                  setMovimentoJaDisparou(true);
                }
                if (confeteNaNota(r.rating)) {
                  dispararConfete();
                }
                onRate(r.rating);
              }}
              onAnimationEnd={() => setDisparando(null)}
              className={`${BUTTON_CLASS} ${r.className} ${
                disparando === r.rating ? r.movimento ?? '' : movimentoJaDisparou ? 'rc-move-rest' : ''
              }`}
            >
              <span className="flex w-full items-center justify-between gap-1.5">
                <span className="flex items-center gap-1.5">
                  <Icon size={14} aria-hidden="true" />
                  {r.label}
                </span>
                <kbd className={KEY_CHIP_CLASS}>{r.key}</kbd>
              </span>
              {interval !== null && (
                <span className="text-xs font-medium tabular-nums">{interval}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
